const DATE_TIME_WITHOUT_ZONE_RE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;
const EXPLICIT_ZONE_RE = /(z|[+-]\d{2}:?\d{2})$/i;

export const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

export const parseUtcTimestampToMs = (value) => {
  if (value == null || value === "") return NaN;

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "number") {
    return value > 1e12 ? value : value * 1000;
  }

  const rawValue = String(value).trim();
  if (!rawValue) return NaN;

  const normalizedValue =
    DATE_TIME_WITHOUT_ZONE_RE.test(rawValue) && !EXPLICIT_ZONE_RE.test(rawValue)
      ? `${rawValue.replace(" ", "T")}Z`
      : rawValue;

  return new Date(normalizedValue).getTime();
};

const formatInUserTimeZone = (value, options) => {
  const timestampMs = parseUtcTimestampToMs(value);
  if (!Number.isFinite(timestampMs)) return "-";

  return new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: getUserTimeZone(),
  }).format(new Date(timestampMs));
};

export const formatUserTimeWithZone = (value = Date.now()) =>
  formatInUserTimeZone(value, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });

// Compact wall-clock time, e.g. "07:30 PM".
export const formatUserClockTime = (value) =>
  formatInUserTimeZone(value, { hour: "2-digit", minute: "2-digit" });

export const formatUserDateTimeWithZone = (value) =>
  formatInUserTimeZone(value, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  });

const getLightweightChartTimeMs = (time) => {
  if (typeof time === "number") return time * 1000;

  if (
    time &&
    typeof time === "object" &&
    Number.isFinite(time.year) &&
    Number.isFinite(time.month) &&
    Number.isFinite(time.day)
  ) {
    return Date.UTC(time.year, time.month - 1, time.day);
  }

  return NaN;
};

export const formatLightweightChartTime = (time, interval = "1m") => {
  const timestampMs = getLightweightChartTimeMs(time);
  const isDailyInterval = ["1d", "1wk", "1mo"].includes(interval);

  return formatInUserTimeZone(timestampMs, {
    month: "short",
    day: "2-digit",
    ...(isDailyInterval
      ? { year: "2-digit" }
      : { hour: "2-digit", minute: "2-digit" }),
  });
};

const normalizeTickMarkType = (tickMarkType) => {
  switch (tickMarkType) {
    case 0:
    case "Year":
      return "year";
    case 1:
    case "Month":
      return "month";
    case 2:
    case "DayOfMonth":
      return "day";
    case 4:
    case "TimeWithSeconds":
      return "timeWithSeconds";
    case 3:
    case "Time":
    default:
      return "time";
  }
};

export const formatLightweightTickLabel = (
  time,
  tickMarkType,
  interval = "1m",
) => {
  const timestampMs = getLightweightChartTimeMs(time);
  if (!Number.isFinite(timestampMs)) return "";

  const normalizedTickType = normalizeTickMarkType(tickMarkType);
  const isDailyInterval = ["1d", "1wk", "1mo"].includes(interval);

  if (normalizedTickType === "year") {
    return formatInUserTimeZone(timestampMs, { year: "numeric" });
  }

  if (normalizedTickType === "month") {
    return formatInUserTimeZone(timestampMs, { month: "short" });
  }

  if (normalizedTickType === "day" || isDailyInterval) {
    return formatInUserTimeZone(timestampMs, {
      month: "short",
      day: "2-digit",
    });
  }

  if (normalizedTickType === "timeWithSeconds") {
    return formatInUserTimeZone(timestampMs, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  }

  return formatInUserTimeZone(timestampMs, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

export const formatChartAxisTime = (value) =>
  formatInUserTimeZone(value, {
    hour: "2-digit",
    minute: "2-digit",
  });
